import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import axiosInstance from "../api/axiosInstance";

/**
 * useFilterFacets
 *
 * Fetches dynamic filter options from GET /api/filters/facets?type=<type>&...filters
 * whenever the active filter params change.
 *
 * @param {string}  type          - "me" | "my-team" | "deputed"
 * @param {object}  activeParams  - flat query params to forward to the facets API
 *                                  e.g. { search, status, priority, departmentId, projectId, ... }
 */
const useFilterFacets = (type = "me", activeParams = {}) => {
    const [facetOptions, setFacetOptions] = useState({
        departments: [],
        projects: [],
        groups: [],
        employees: [],   // kept for backward-compat (mapped from assignees)
        assignees: [],
        assigners: [],
        statuses: [],
        priorities: [],
        labels: [],
    });
    const [isLoadingFacets, setIsLoadingFacets] = useState(false);

    const abortControllerRef = useRef(null);

    const fetchFacets = useCallback(async (params) => {
        // Abort any in-flight request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setIsLoadingFacets(true);
        try {
            const { data } = await axiosInstance.get("/api/filters/facets", {
                params: { type, ...params },
                signal: abortControllerRef.current.signal,
            });

            if (data?.success && data?.data) {
                const raw = data.data;
                const assignees = Array.isArray(raw.assignees) ? raw.assignees : [];
                setFacetOptions({
                    departments: Array.isArray(raw.departments) ? raw.departments : [],
                    projects: Array.isArray(raw.projects) ? raw.projects : [],
                    groups: Array.isArray(raw.groups) ? raw.groups : [],
                    assignees,
                    assigners: Array.isArray(raw.assigners) ? raw.assigners : [],
                    // backward-compat alias used by filterOptionsByCategory
                    employees: assignees,
                    statuses: Array.isArray(raw.statuses)
                        ? raw.statuses.map((s) => s.status).filter(Boolean)
                        : [],
                    priorities: Array.isArray(raw.priorities)
                        ? raw.priorities.map((p) => p.priority).filter(Boolean)
                        : [],
                    labels: Array.isArray(raw.labels) ? raw.labels : [],
                });
            }
        } catch (err) {
            if (axios.isCancel(err) || err.name === "CanceledError" || err.name === "AbortError") return;
            console.error("[useFilterFacets] Failed to fetch facet options:", err);
        } finally {
            setIsLoadingFacets(false);
        }
    }, [type]);

    // Stable serialisation so the effect only fires when params actually change
    const paramsKey = JSON.stringify(activeParams);

    useEffect(() => {
        fetchFacets(activeParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, paramsKey]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) abortControllerRef.current.abort();
        };
    }, []);

    return { facetOptions, isLoadingFacets };
};

export default useFilterFacets;
